import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getErrorMessage } from '@/api/api';
import { echoService } from '@/services/echoService';
import type {
  UploadedFile,
  TrainingLink,
  PersonalityConfig,
  ScrapingQualityReport,
} from '@/types/echo_creation';
import type { RetrainBenchmark, StepStatus, TrainingStep } from '../types';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/hooks/useAuth';
import { ACCOUNT_BILLING_CREDITS_PATH } from '@/routes/paths';
import { convertToWebp } from '@/utils/image';

/** Validate file size per media type. Returns error message or null. */
const validateFile = (file: File): string | null => {
  const sizeMB = file.size / (1024 * 1024);
  const type = file.type;

  // Video: 500MB
  if (type.startsWith('video/')) {
    if (sizeMB > 500) return 'Video size exceeds 500MB limit.';
  }
  // Audio: 100MB
  else if (type.startsWith('audio/')) {
    if (sizeMB > 100) return 'Audio size exceeds 100MB limit.';
  }
  // Image: 20MB
  else if (type.startsWith('image/')) {
    if (sizeMB > 20) return 'Image size exceeds 20MB limit.';
  }
  // PDF/Doc: 50MB (Backend default for 'document')
  else if ((type === 'application/pdf' || type.includes('document')) && sizeMB > 50) {
    return 'Document size exceeds 50MB limit.';
  }

  return null;
};

interface UseTrainingOptions {
  onSuccess?: () => void;
}

interface ApiErrorResponse {
  status?: number;
  message?: string;
  detail?: string;
}

const STEP_LABELS = [
  'Collecting web data...',
  'Processing uploaded files...',
  'Generating personality profile...',
  'Loading results...',
];

interface TrainingStreamFlags {
  streamCompleted: boolean;
  hadError: boolean;
  qualityScore?: Record<string, unknown> | null;
}

/**
 * Manages the Training step: file uploads, link management, web scraping,
 * streaming training with SSE progress, and reconnection after page refresh.
 */
export const useTraining = (draftId: string, options?: UseTrainingOptions) => {
  const { addToast } = useToast();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [files, setFiles] = React.useState<UploadedFile[]>([]);
  const [links, setLinks] = React.useState<TrainingLink[]>([]);

  // Separate loading/processing states
  const [isLoadingData, setIsLoadingData] = React.useState(true);
  const [isScraping, setIsScraping] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const [filesResp, profileResp] = await Promise.all([
          api.echoes.listTrainingFiles(draftId),
          api.echoes.getDraftProfile(draftId),
        ]);

        if (isMounted) {
          const mappedFiles: UploadedFile[] = filesResp.files.map((file) => ({
            ...file,
            status: 'completed',
            progress: 100,
            name: file.filename,
          }));
          setFiles(mappedFiles);

          setLinks(profileResp.training_links || []);
        }
      } catch {
        addToast({
          title: 'Error Loading Data',
          subtitle: 'Could not fetch existing training data.',
          type: 'error',
        });
      } finally {
        if (isMounted) setIsLoadingData(false);
      }
    };

    if (draftId) loadData();

    return () => {
      isMounted = false;
    };
  }, [draftId, addToast]);

  const addFile = React.useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        addToast({
          title: 'File Too Large',
          subtitle: validationError,
          type: 'error',
        });
        return;
      }

      const uploadFile = file.type.startsWith('image/') ? await convertToWebp(file) : file;

      const tempId = `temp-${Date.now()}`;

      const newFile: UploadedFile = {
        id: tempId,
        name: uploadFile.name,
        size: uploadFile.size,
        type: uploadFile.type,
        filename: uploadFile.name,
        url: '',
        processing_status: 'uploaded',
        status: 'uploading',
        progress: 0,
      };

      setFiles((prev) => [...prev, newFile]);

      try {
        const response = await api.echoes.uploadTrainingFile(draftId, uploadFile, (percent) => {
          setFiles((prev) =>
            prev.map((item) => (item.id === tempId ? { ...item, progress: percent } : item))
          );
        });

        setFiles((prev) =>
          prev.map((existing) =>
            existing.id === tempId
              ? {
                  ...response,
                  status: 'completed',
                  progress: 100,
                  name: file.name,
                }
              : existing
          )
        );

        addToast({ title: 'File Uploaded', subtitle: file.name, type: 'success' });
      } catch (error) {
        setFiles((prev) =>
          prev.map((item) =>
            item.id === tempId ? { ...item, status: 'error', progress: 0 } : item
          )
        );
        addToast({
          title: 'Upload Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    },
    [draftId, addToast]
  );

  const removeFile = React.useCallback(
    async (fileId: string) => {
      const fileToRemove = files.find((file) => file.id === fileId);
      setFiles((prev) => prev.filter((file) => file.id !== fileId));

      if (fileId.startsWith('temp-')) return;

      try {
        await api.echoes.deleteTrainingFile(draftId, fileId);
      } catch {
        addToast({
          title: 'Delete Failed',
          subtitle: 'Could not delete file from server.',
          type: 'error',
        });
        if (fileToRemove) setFiles((prev) => [...prev, fileToRemove]);
      }
    },
    [draftId, files, addToast]
  );

  const retryUpload = React.useCallback(
    (fileId: string) => {
      setFiles((prev) => prev.filter((file) => file.id !== fileId));
      addToast({
        title: 'Retry',
        subtitle: 'Please select the file again to upload.',
        type: 'info',
      });
    },
    [addToast]
  );

  const toggleScreenshotFlag = React.useCallback(
    async (fileId: string, value: boolean) => {
      // Optimistic update
      setFiles((prev) =>
        prev.map((file) => (file.id === fileId ? { ...file, contains_screenshots: value } : file))
      );

      try {
        await api.echoes.toggleScreenshotFlag(draftId, fileId, value);
      } catch {
        // Revert on failure
        setFiles((prev) =>
          prev.map((file) =>
            file.id === fileId ? { ...file, contains_screenshots: !value } : file
          )
        );
        addToast({
          title: 'Update Failed',
          subtitle: 'Could not update screenshot flag.',
          type: 'error',
        });
      }
    },
    [draftId, addToast]
  );

  const addLink = React.useCallback(
    async (url: string) => {
      const tempId = `temp-link-${Date.now()}`;
      const newLink: TrainingLink = { id: tempId, url, link_type: 'website' };
      setLinks((prev) => [...prev, newLink]);

      try {
        const payload = { links: [{ url, link_type: 'website' }] };
        const response = await api.echoes.addTrainingLinks(draftId, payload);

        if (response.training_links) {
          setLinks(response.training_links);
        }

        addToast({ title: 'Link Added', subtitle: url, type: 'success' });
      } catch (error) {
        setLinks((prev) => prev.filter((link) => link.id !== tempId));
        addToast({
          title: 'Failed to add link',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    },
    [draftId, addToast]
  );

  const removeLink = React.useCallback(
    async (linkId: string) => {
      const linkToRemove = links.find((link) => link.id === linkId);
      setLinks((prev) => prev.filter((link) => link.id !== linkId));

      try {
        await api.echoes.removeTrainingLink(draftId, linkId);
      } catch {
        if (linkToRemove) setLinks((prev) => [...prev, linkToRemove]);
        addToast({
          title: 'Remove Failed',
          subtitle: 'Could not remove link.',
          type: 'error',
        });
      }
    },
    [draftId, links, addToast]
  );

  const startScraping = React.useCallback(async () => {
    setIsScraping(true);
    try {
      const response = await api.echoes.collectFromInternet(draftId);

      if (response.success && user) {
        const newCredits = response.credits_remaining;
        updateUser({ ...user, credits: newCredits });

        addToast({
          title: 'Scraping Started',
          subtitle: `Cost: ${response.credits_charged} credits. Processing in background...`,
          type: 'success',
        });
      }
    } catch (error) {
      const apiError = error as ApiErrorResponse;
      if (apiError.status === 402) {
        addToast({
          title: 'Insufficient Credits',
          subtitle: apiError.message || 'You need more credits to use this feature.',
          type: 'error',
          action: {
            label: 'Buy Credits',
            onClick: () => navigate(ACCOUNT_BILLING_CREDITS_PATH),
          },
        });
      } else {
        addToast({
          title: 'Scraping Failed',
          subtitle: getErrorMessage(error),
          type: 'error',
        });
      }
    } finally {
      setIsScraping(false);
    }
  }, [draftId, user, updateUser, addToast, navigate]);

  const trainEcho = React.useCallback(
    async (collectTier?: 'free' | 'premium') => {
      try {
        const response = await api.echoes.train(draftId, collectTier);
        let subtitle = response.message || 'Echo trained successfully.';
        if (response.quality_score) {
          const score = response.quality_score.score;
          subtitle = `Personality ${score}% complete. ${score < 100 ? 'Adding training files can improve it further.' : 'Fully formed personality!'}`;
        }

        addToast({
          title: 'Training Complete!',
          subtitle,
          type: 'success',
        });

        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          navigate('/my-echo');
        }
      } catch (error) {
        const msg = getErrorMessage(error);
        const apiError = error as ApiErrorResponse;

        if (apiError.status === 402) {
          addToast({
            title: 'Insufficient Credits',
            subtitle: msg || 'You need more credits for premium scraping.',
            type: 'error',
            action: {
              label: 'Buy Credits',
              onClick: () => navigate(ACCOUNT_BILLING_CREDITS_PATH),
            },
          });
        } else if (apiError.status === 400) {
          addToast({ title: 'Cannot Train', subtitle: msg, type: 'error' });
        } else {
          addToast({ title: 'Training Failed', subtitle: msg, type: 'error' });
        }

        throw error;
      }
    },
    [draftId, options, navigate, addToast]
  );

  const [trainingSteps, setTrainingSteps] = React.useState<TrainingStep[]>([]);
  const [trainingDone, setTrainingDone] = React.useState(false);
  const [trainingError, setTrainingError] = React.useState<string | null>(null);
  const [preloadedConfig, setPreloadedConfig] = React.useState<PersonalityConfig | null>(null);
  const [scrapingQualityReport, setScrapingQualityReport] =
    React.useState<ScrapingQualityReport | null>(null);
  const [retrainBenchmark, setRetrainBenchmark] = React.useState<RetrainBenchmark | null>(null);
  const [idleVideoStatus, setIdleVideoStatus] = React.useState<
    'none' | 'processing' | 'done' | 'failed'
  >('none');
  const idleVideoPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  /** Stop polling the idle video status endpoint and clear the interval. */
  const stopIdleVideoPolling = React.useCallback(() => {
    if (idleVideoPollRef.current) {
      clearInterval(idleVideoPollRef.current);
      idleVideoPollRef.current = null;
    }
  }, []);

  /** Begin polling GET /idle-video/status every 5s. Stops on terminal status. */
  const startIdleVideoPolling = React.useCallback(() => {
    stopIdleVideoPolling();
    setIdleVideoStatus('processing');

    const poll = async () => {
      try {
        const res = await echoService.getIdleVideoStatus(draftId);
        setIdleVideoStatus(res.status);
        if (res.status === 'done' || res.status === 'failed' || res.status === 'none') {
          stopIdleVideoPolling();
        }
      } catch {
        // Non-fatal — keep polling
      }
    };

    // Initial check after 3s (give backend time to start the task)
    const initialTimeout = setTimeout(() => {
      void poll();
      idleVideoPollRef.current = setInterval(() => void poll(), 5000);
    }, 3000);

    // Store the timeout so we can clear it on stop
    idleVideoPollRef.current = initialTimeout as unknown as ReturnType<typeof setInterval>;
  }, [draftId, stopIdleVideoPolling]);

  const userRef = React.useRef(user);
  userRef.current = user;
  const updateUserRef = React.useRef(updateUser);
  updateUserRef.current = updateUser;

  /** Shared SSE event handler for trainEchoStream and reconnectToTraining. */
  const handleTrainingEvent = React.useCallback(
    (event: string, data: Record<string, unknown>, flags: TrainingStreamFlags) => {
      switch (event) {
        case 'credits_charged': {
          const remaining = data['credits_remaining'] as number;
          if (userRef.current) {
            updateUserRef.current({ ...userRef.current, credits: remaining });
          }
          break;
        }
        case 'train_start': {
          const total = (data['total_steps'] as number) || 4;
          setTrainingSteps(
            Array.from({ length: total }, (_, i) => ({
              label: STEP_LABELS[i] || `Step ${i + 1}...`,
              status: 'pending' as StepStatus,
            }))
          );
          break;
        }
        case 'step_start': {
          const step = data['step'] as number;
          const label = data['label'] as string;
          setTrainingSteps((prev) =>
            prev.map((ts, idx) => (idx === step ? { label, status: 'active' } : ts))
          );
          break;
        }
        case 'step_update': {
          const step = data['step'] as number;
          const label = data['label'] as string;
          setTrainingSteps((prev) =>
            prev.map((ts, idx) => (idx === step ? { ...ts, label, status: 'active' } : ts))
          );
          break;
        }
        case 'step_done': {
          const step = data['step'] as number;
          setTrainingSteps((prev) =>
            prev.map((ts, idx) => (idx === step ? { ...ts, status: 'done' } : ts))
          );
          if (step === 0 && data['quality_report']) {
            setScrapingQualityReport(data['quality_report'] as ScrapingQualityReport);
          }
          break;
        }
        case 'step_skip': {
          const step = data['step'] as number;
          const label = (data['label'] as string) || 'Skipped';
          setTrainingSteps((prev) =>
            prev.map((ts, idx) => (idx === step ? { label, status: 'skipped' } : ts))
          );
          break;
        }
        case 'train_complete': {
          flags.streamCompleted = true;
          flags.qualityScore =
            (data['quality_score'] as Record<string, unknown> | null | undefined) ?? null;
          const before = Number(flags.qualityScore?.['before_completeness_score']);
          const after = Number(flags.qualityScore?.['completeness_score']);
          if (Number.isFinite(before) && Number.isFinite(after)) {
            setRetrainBenchmark({ before, after, delta: after - before });
          }
          break;
        }
        case 'train_error': {
          flags.hadError = true;
          setTrainingError((data['detail'] as string) || 'Training failed.');
          setTrainingSteps((prev) =>
            prev.map((step) => (step.status === 'active' ? { ...step, status: 'error' } : step))
          );
          break;
        }
        default:
          break;
      }
    },
    []
  );

  /** Post-stream: fetch personality preview and mark training done. */
  const finalizeTrainingStream = React.useCallback(
    async (
      streamCompleted: boolean,
      hadError: boolean,
      signal?: AbortSignal,
      targetDraftId: string = draftId
    ) => {
      if (!streamCompleted || hadError) return;

      setTrainingSteps((prev) =>
        prev.map((ts, idx) =>
          idx === prev.length - 1
            ? { ...ts, label: 'Loading personality preview...', status: 'active' }
            : ts
        )
      );

      try {
        if (!signal?.aborted) {
          const preview = await api.echoes.getPersonalityPreview(targetDraftId);
          if (!signal?.aborted) setPreloadedConfig(preview);
        }
      } catch {
        // Non-fatal — PersonalityConfigSection fetches as fallback
      }

      if (signal?.aborted) return;

      setTrainingSteps((prev) =>
        prev.map((ts, idx) =>
          idx === prev.length - 1 ? { ...ts, label: 'Results loaded', status: 'done' } : ts
        )
      );
      setTrainingDone(true);
    },
    [draftId]
  );

  const trainEchoStream = React.useCallback(
    async (collectTier: 'free' | 'premium') => {
      // Reset state
      setTrainingDone(false);
      setTrainingError(null);
      setTrainingSteps([]);
      setPreloadedConfig(null);
      setRetrainBenchmark(null);
      startIdleVideoPolling();

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const flags = { streamCompleted: false, hadError: false };

      try {
        await api.echoes.trainStream(
          draftId,
          collectTier,
          (event, data) => handleTrainingEvent(event, data, flags),
          controller.signal
        );

        await finalizeTrainingStream(flags.streamCompleted, flags.hadError, controller.signal);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;

        const msg = getErrorMessage(error);
        const apiError = error as ApiErrorResponse;

        if (apiError.status === 402) {
          addToast({
            title: 'Insufficient Credits',
            subtitle: msg || 'You need more credits for premium scraping.',
            type: 'error',
            action: {
              label: 'Buy Credits',
              onClick: () => navigate(ACCOUNT_BILLING_CREDITS_PATH),
            },
          });
        } else if (apiError.status === 400) {
          addToast({ title: 'Cannot Train', subtitle: msg, type: 'error' });
        }

        setTrainingError(msg || 'Training failed. Please try again.');
      }
    },
    [
      draftId,
      addToast,
      navigate,
      handleTrainingEvent,
      finalizeTrainingStream,
      startIdleVideoPolling,
    ]
  );

  /** Reconnect to an in-progress training after page refresh. */
  const reconnectToTraining = React.useCallback(async () => {
    setTrainingDone(false);
    setTrainingError(null);
    setTrainingSteps([]);
    setPreloadedConfig(null);
    setRetrainBenchmark(null);
    startIdleVideoPolling();

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const flags = { streamCompleted: false, hadError: false };

    try {
      await api.echoes.observeTrainingStream(
        draftId,
        (event, data) => handleTrainingEvent(event, data, flags),
        controller.signal
      );

      await finalizeTrainingStream(flags.streamCompleted, flags.hadError, controller.signal);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      setTrainingError(getErrorMessage(error) || 'Failed to reconnect to training.');
    }
  }, [draftId, handleTrainingEvent, finalizeTrainingStream, startIdleVideoPolling]);

  /** Retrain a published Echo using the backend's real background progress stream. */
  const retrainEcho = React.useCallback(
    async (targetDraftId: string, collectTier: 'free' | 'premium' = 'free') => {
      setTrainingDone(false);
      setTrainingError(null);
      setTrainingSteps([]);
      setPreloadedConfig(null);
      setScrapingQualityReport(null);
      setRetrainBenchmark(null);
      startIdleVideoPolling();

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const flags: TrainingStreamFlags = {
        streamCompleted: false,
        hadError: false,
      };

      try {
        await echoService.retrainStream(
          targetDraftId,
          collectTier,
          (event, data) => handleTrainingEvent(event, data, flags),
          controller.signal
        );

        await finalizeTrainingStream(
          flags.streamCompleted,
          flags.hadError,
          controller.signal,
          targetDraftId
        );

        if (flags.streamCompleted && !flags.hadError && flags.qualityScore) {
          const after = Number(flags.qualityScore['completeness_score']);
          const before = Number(flags.qualityScore['before_completeness_score']);
          const hasComparison = Number.isFinite(before) && Number.isFinite(after);
          addToast({
            title: 'Retrain Complete!',
            subtitle: hasComparison
              ? `Completeness improved from ${before.toFixed(1)}% to ${after.toFixed(1)}% (${after - before >= 0 ? '+' : ''}${(after - before).toFixed(1)}).`
              : Number.isFinite(after)
                ? `Personality ${after.toFixed(1)}% complete.`
                : 'The improved personality is now live.',
            type: 'success',
          });
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;

        setTrainingSteps((prev) =>
          prev.map((ts) => (ts.status === 'active' ? { ...ts, status: 'error' } : ts))
        );
        const msg = getErrorMessage(error);
        const apiError = error as ApiErrorResponse;
        if (apiError.status === 402) {
          addToast({
            title: 'Insufficient Credits',
            subtitle: msg || 'You need more credits for premium retraining.',
            type: 'error',
            action: {
              label: 'Buy Credits',
              onClick: () => navigate(ACCOUNT_BILLING_CREDITS_PATH),
            },
          });
        }
        setTrainingError(msg || 'Retrain failed. Please try again.');
      }
    },
    [addToast, finalizeTrainingStream, handleTrainingEvent, navigate, startIdleVideoPolling]
  );

  React.useEffect(
    () => () => {
      abortRef.current?.abort();
      stopIdleVideoPolling();
    },
    [stopIdleVideoPolling]
  );

  return {
    files,
    setFiles,
    links,
    isProcessing: isScraping || isLoadingData,
    isLoadingData,
    addFile,
    removeFile,
    retryUpload,
    toggleScreenshotFlag,
    addLink,
    removeLink,
    startScraping,
    trainEcho,
    // Streaming training
    trainingSteps,
    trainingDone,
    trainingError,
    trainEchoStream,
    preloadedConfig,
    reconnectToTraining,
    scrapingQualityReport,
    retrainBenchmark,
    // Retrain (published echo)
    retrainEcho,
    // Idle video
    idleVideoStatus,
  };
};
